// v2.1 (D): which screen the auth gate shows. Pure (no DOM, no React), so it is unit tested on its own.
//
//   signed out on "/"          -> the Landing (workstream C)
//   signed out elsewhere       -> sign in (Clerk and/or the password form, by authMode); "/signup" and
//                                 "/invite/:code" -> sign up
//   signed in, access pending  -> enter an invite code / request access
//   signed in, suspended       -> the same screen, saying access is paused (request access only)
//   signed in, pending, made from a country where sign-ups are closed (v4 LG2)
//                              -> "not available in your country yet": the waitlist (an invite code still works)
//   signed in, Terms not accepted (a new account, or the Terms changed since; v4 LG2)
//                              -> the Terms checkbox, before the pending screen or the app
//   signed in                  -> the app
//   signed out on "/signup" from a country where sign-ups are closed -> the waitlist (an invite link still signs up)
// Clerk is used only when /api/config gave a publishable key and authMode isn't "password"; with no key the app
// behaves exactly as before (password form, invite links).

export type AuthMode = "password" | "clerk" | "both";
/** GET /api/config */
export type AuthConfig = {
  clerkPublishableKey: string | null;
  authMode: AuthMode;
  /** the legal pages are public unless this is false (LEGAL_PAGES_PUBLIC=0) */
  legalPagesPublic?: boolean;
  /** v4 LG2: new accounts from this visitor's country wait for an invite during the beta */
  signupBlocked?: boolean;
};
/** Clerk's state in the browser (null when Clerk isn't used). `failed`: clerk-js didn't load in time. */
export type ClerkStatus = { loaded: boolean; signedIn: boolean; failed: boolean };

export type GateInput = {
  config: AuthConfig | null;
  clerk: ClerkStatus | null;
  /** the store's session state (GET /api/me) */
  auth: "unknown" | "in" | "out";
  access?: "active" | "pending" | "suspended" | null;
  /** v4 LG2: /api/me user.geoBlocked and user.termsRequired */
  geoBlocked?: boolean;
  termsRequired?: boolean;
  path: string;
};

export type Methods = { clerk: boolean; password: boolean };
export type Gate =
  | { screen: "splash" }
  | { screen: "landing" }
  | ({ screen: "sign-in" } & Methods)
  | ({ screen: "sign-up" } & Methods)
  | ({ screen: "invite"; code: string } & Methods)
  | { screen: "pending"; suspended?: true; geo?: true }
  /** v4 LG2: the Terms checkbox */
  | { screen: "terms" }
  /** v4 LG2: signed out on /signup from a country where sign-ups are closed */
  | { screen: "signup-closed" }
  /** "rejected": Clerk says signed in but the api refused the session; "unavailable": Clerk-only and clerk-js failed */
  | { screen: "session-error"; reason: "rejected" | "unavailable" }
  | { screen: "app" };

export const PASSWORD_ONLY: AuthConfig = { clerkPublishableKey: null, authMode: "password" };

export const clerkEnabled = (c: AuthConfig | null): boolean => !!c?.clerkPublishableKey && c.authMode !== "password";
/** The password form is offered unless Clerk is on and the mode is "clerk" (no key = password, whatever the mode). */
export const passwordEnabled = (c: AuthConfig | null): boolean => !clerkEnabled(c) || c!.authMode !== "clerk";

/** Normalises whatever /api/config sent. */
export function parseConfig(v: unknown): AuthConfig {
  const o = (typeof v === "object" && v !== null ? v : {}) as Record<string, unknown>;
  const key = typeof o.clerkPublishableKey === "string" && o.clerkPublishableKey.trim() ? o.clerkPublishableKey.trim() : null;
  const mode: AuthMode = o.authMode === "clerk" || o.authMode === "both" ? o.authMode : "password";
  return {
    clerkPublishableKey: key,
    authMode: key ? mode : "password",
    ...(o.legalPagesPublic === false ? { legalPagesPublic: false } : {}),
    ...(o.signupBlocked === true ? { signupBlocked: true } : {}),
  };
}

export function gate(i: GateInput): Gate {
  if (!i.config) return { screen: "splash" };
  const clerk = clerkEnabled(i.config) && !i.clerk?.failed;
  const password = passwordEnabled(i.config);
  if (clerk && !i.clerk?.loaded) return { screen: "splash" };
  if (i.auth === "unknown") return { screen: "splash" };
  if (i.auth === "in") {
    if (i.access === "suspended") return { screen: "pending", suspended: true };
    if (i.access === "pending" && i.geoBlocked) return { screen: "pending", geo: true };
    if (i.termsRequired) return { screen: "terms" };
    return i.access === "pending" ? { screen: "pending" } : { screen: "app" };
  }
  // signed out as far as the api is concerned
  if (clerk && i.clerk?.signedIn) return { screen: "session-error", reason: "rejected" };
  const path = i.path.replace(/\/+$/, "") || "/";
  if (path === "/") return { screen: "landing" };
  if (!clerk && !password) return { screen: "session-error", reason: "unavailable" };
  const m: Methods = { clerk, password };
  const invite = path.match(/^\/invite\/([^/]+)$/);
  if (invite) return { screen: "invite", code: decodeURIComponent(invite[1]!), ...m };
  if (path === "/signup") return i.config.signupBlocked ? { screen: "signup-closed" } : clerk ? { screen: "sign-up", ...m } : { screen: "sign-in", ...m };
  return { screen: "sign-in", ...m };
}
